/**
 * Excel Import/Export Utilities for Top Priority App
 * 
 * Provides functions for exporting analysis data to Excel and
 * importing previously exported Excel files.
 */

const SHEETJS_CDN = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

/**
 * Load SheetJS library on demand
 */
async function loadSheetJS() {
	if (window.XLSX) return window.XLSX;
	
	await new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = SHEETJS_CDN;
		script.onload = resolve;
		script.onerror = reject;
		document.head.appendChild(script);
	});
	
	return window.XLSX;
}

/**
 * Generate timestamp string for filenames
 * Format: YYYY-MM-DD_HH-MM
 */
export function getTimestamp() {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const day = String(now.getDate()).padStart(2, '0');
	const hours = String(now.getHours()).padStart(2, '0');
	const minutes = String(now.getMinutes()).padStart(2, '0');
	return `${year}-${month}-${day}_${hours}-${minutes}`;
}

/**
 * Sanitize text to prevent Excel formula injection
 * Prefixes dangerous characters with single quote
 */
export function sanitizeForExcel(value) {
	if (value === null || value === undefined) return '';
	const str = String(value);
	// Prefix with single quote if starts with formula-triggering characters
	if (/^[=+\-@\t\r]/.test(str)) {
		return "'" + str;
	}
	return str;
}

/**
 * Unsanitize text imported from Excel
 * Removes leading single quote added for formula injection prevention
 */
export function unsanitizeFromExcel(value) {
	if (value === null || value === undefined) return '';
	const str = String(value);
	// Remove leading single quote that was added for formula injection prevention
	if (str.startsWith("'") && /^'[=+\-@\t\r]/.test(str)) {
		return str.slice(1);
	}
	return str;
}

/**
 * Export analysis data to Excel file
 * 
 * @param {Object} data - Export data
 * @param {string} data.dilemma - Mind dump / context text
 * @param {string} data.templateName - Name of template used (or 'Custom')
 * @param {number} data.numProjects - Number of projects
 * @param {number} data.numCriteria - Number of criteria
 * @param {Array} data.criteria - Array of criteria objects with name, polarity, category
 * @param {Array} data.priorityRanking - Array of ranked items with name, score, values
 * @param {Function} data.getFocusMatrixQuadrant - Function to get quadrant for a project
 * @param {string} [data.filename] - Optional custom filename (without extension)
 */
export async function exportToExcel(data) {
	const {
		dilemma,
		templateName,
		numProjects,
		numCriteria,
		criteria,
		priorityRanking,
		getFocusMatrixQuadrant,
		filename = `TopPriority-${getTimestamp()}`
	} = data;

	const XLSX = await loadSheetJS();
	const workbook = XLSX.utils.book_new();

	// === Sheet 1: Metadata ===
	const metadataRows = [
		['Top Priority Analysis Export'],
		[],
		['Export Date', new Date().toLocaleString()],
		['Template Used', templateName || 'Custom'],
		['Number of Projects', numProjects],
		['Number of Criteria', numCriteria],
		[],
		['Mind Dump / Context'],
		[sanitizeForExcel(dilemma) || '(No context provided)']
	];
	const metadataSheet = XLSX.utils.aoa_to_sheet(metadataRows);
	metadataSheet['!cols'] = [{ wch: 25 }, { wch: 60 }];
	XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Metadata');

	// === Sheet 2: Criteria ===
	const criteriaHeaders = ['Criteria Name', 'Polarity', 'Category'];
	const criteriaRows = criteria.map(c => [
		sanitizeForExcel(c.name),
		c.polarity || 'Higher is better',
		c.category || 'Important'
	]);
	const criteriaSheet = XLSX.utils.aoa_to_sheet([criteriaHeaders, ...criteriaRows]);
	criteriaSheet['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 12 }];
	XLSX.utils.book_append_sheet(workbook, criteriaSheet, 'Criteria');

	// === Sheet 3: Scores ===
	const criteriaNames = criteria.map(c => c.name);
	const scoresHeaders = ['Project Name', ...criteriaNames];
	const scoresRows = priorityRanking.map(item => {
		const scores = item.values || [];
		return [sanitizeForExcel(item.name), ...scores];
	});
	const scoresSheet = XLSX.utils.aoa_to_sheet([scoresHeaders, ...scoresRows]);
	scoresSheet['!cols'] = [{ wch: 25 }, ...criteriaNames.map(() => ({ wch: 18 }))];
	XLSX.utils.book_append_sheet(workbook, scoresSheet, 'Scores');

	// === Sheet 4: Results ===
	const resultsHeaders = ['Priority Rank', 'Project Name', 'Priority Score', 'Focus Matrix Quadrant'];
	const resultsRows = priorityRanking.map((item, index) => [
		index + 1,
		sanitizeForExcel(item.name),
		Math.round(item.score * 10) / 10,
		getFocusMatrixQuadrant(item.name)
	]);
	const resultsSheet = XLSX.utils.aoa_to_sheet([resultsHeaders, ...resultsRows]);
	resultsSheet['!cols'] = [{ wch: 12 }, { wch: 25 }, { wch: 14 }, { wch: 22 }];
	XLSX.utils.book_append_sheet(workbook, resultsSheet, 'Results');

	// Generate and download the file
	const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
	const blob = new Blob([excelBuffer], { 
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
	});
	
	const link = document.createElement('a');
	link.download = `${filename}.xlsx`;
	link.href = URL.createObjectURL(blob);
	link.click();
	
	// Clean up
	setTimeout(() => URL.revokeObjectURL(link.href), 100);
}

/**
 * Parse an Excel file and extract analysis data
 * 
 * @param {File} file - The Excel file to parse
 * @returns {Promise<Object>} Parsed data object
 * @throws {Error} If file is invalid or parsing fails
 */
export async function parseExcelFile(file) {
	// Validate file size
	if (file.size > MAX_FILE_SIZE) {
		throw new Error('File is too large. Maximum size is 2MB.');
	}

	// Validate file type
	if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
		throw new Error('Please select an Excel file (.xlsx or .xls)');
	}

	const XLSX = await loadSheetJS();
	
	// Read the file
	const arrayBuffer = await file.arrayBuffer();
	const workbook = XLSX.read(arrayBuffer, { type: 'array' });

	// Check for required sheets
	const sheetNames = workbook.SheetNames;
	const hasCriteria = sheetNames.includes('Criteria');
	const hasScores = sheetNames.includes('Scores');

	if (!hasCriteria || !hasScores) {
		throw new Error('Invalid file format. The Excel file must contain "Criteria" and "Scores" sheets.');
	}

	// Parse Criteria sheet
	const criteriaSheet = workbook.Sheets['Criteria'];
	const criteriaData = XLSX.utils.sheet_to_json(criteriaSheet);

	if (criteriaData.length === 0) {
		throw new Error('The Criteria sheet is empty.');
	}

	// Parse Scores sheet
	const scoresSheet = workbook.Sheets['Scores'];
	const scoresData = XLSX.utils.sheet_to_json(scoresSheet);

	if (scoresData.length === 0) {
		throw new Error('The Scores sheet is empty.');
	}

	// Extract criteria
	const criteria = criteriaData.map(row => ({
		name: unsanitizeFromExcel(row['Criteria Name'] || ''),
		polarity: row['Polarity'] || 'Higher is better',
		category: row['Category'] || 'Important'
	})).filter(c => c.name);

	if (criteria.length === 0) {
		throw new Error('No valid criteria found in the Criteria sheet.');
	}

	// Get criteria names for column matching
	const criteriaNames = criteria.map(c => c.name);

	// Validate that Scores sheet has matching columns
	const scoresColumns = Object.keys(scoresData[0] || {}).filter(col => col !== 'Project Name');
	
	// Check if criteria count matches score columns
	if (scoresColumns.length !== criteriaNames.length) {
		throw new Error(
			`Criteria count mismatch: Found ${criteriaNames.length} criteria but ${scoresColumns.length} score columns. ` +
			'Please ensure the file was exported correctly.'
		);
	}

	// Validate column names match
	const missingColumns = criteriaNames.filter(name => !scoresColumns.includes(name));
	if (missingColumns.length > 0) {
		throw new Error(`Criteria columns mismatch: Missing columns for: ${missingColumns.join(', ')}`);
	}

	// Build projects data from Scores sheet
	const projects = scoresData.map((row, index) => {
		const name = unsanitizeFromExcel(row['Project Name'] || `Project ${index + 1}`);
		const scores = criteriaNames.map(criteriaName => {
			const score = Number(row[criteriaName]);
			return isNaN(score) ? 50 : Math.max(0, Math.min(100, score));
		});
		return { name, scores };
	});

	// Try to extract dilemma from Metadata sheet
	let dilemma = '';
	if (sheetNames.includes('Metadata')) {
		const metadataSheet = workbook.Sheets['Metadata'];
		const metadataData = XLSX.utils.sheet_to_json(metadataSheet, { header: 1 });
		
		// Look for Mind Dump row
		for (let i = 0; i < metadataData.length; i++) {
			if (metadataData[i]?.[0] === 'Mind Dump / Context' && metadataData[i + 1]) {
				const dilemmaText = unsanitizeFromExcel(metadataData[i + 1][0] || '');
				if (dilemmaText && dilemmaText !== '(No context provided)') {
					dilemma = dilemmaText;
				}
				break;
			}
		}
	}

	return {
		criteria,
		projects,
		dilemma,
		hasMetadata: sheetNames.includes('Metadata')
	};
}
